import {
  ArrowUpRight,
  Eye,
  Globe,
  MousePointerClick,
  Search,
  Target,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from '@/hooks/use-translation'

type QueryPerformance = {
  query: string
  clicks: number
  impressions: number
  ctr: number
  averagePosition: number
}

type GscPageSearchPerformanceResult = {
  siteUrl: string
  pageUrl: string
  dateRange: {
    startDate: string
    endDate: string
  }
  summary: {
    clicks: number
    impressions: number
    ctr: number
    averagePosition: number
  }
  topQueries: QueryPerformance[]
  hasData: boolean
  matchesConnectedProperty?: boolean
  message?: string | null
}

type Props = {
  result: GscPageSearchPerformanceResult | null | undefined
}

type MetricTileProps = {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
}

function MetricTile({ label, value, icon: Icon }: MetricTileProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
      </div>
      <div className="text-sm font-semibold text-card-foreground">{value}</div>
    </div>
  )
}

function formatInteger(value: number) {
  return new Intl.NumberFormat().format(value)
}

function formatCtr(value: number) {
  return `${(value * 100).toFixed(1)}%`
}

function formatPosition(value: number) {
  return value.toFixed(1)
}

function simplifyProperty(siteUrl: string) {
  if (siteUrl.startsWith('sc-domain:')) {
    return siteUrl.replace('sc-domain:', '')
  }

  try {
    return new URL(siteUrl).hostname
  } catch {
    return siteUrl
  }
}

function simplifyPage(pageUrl: string) {
  try {
    const parsedUrl = new URL(pageUrl)
    return `${parsedUrl.hostname}${parsedUrl.pathname}`
  } catch {
    return pageUrl
  }
}

export function GscPageSearchPerformanceCard({ result }: Props) {
  const t = useTranslation()

  if (!result) return null

  const isPropertyMismatch = result.matchesConnectedProperty === false
  const showEmptyState = !result.hasData || isPropertyMismatch

  return (
    <div className="my-1.5 rounded-xl border border-border bg-card p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <Search className="h-4 w-4 shrink-0 text-primary" />
            <span className="truncate text-sm font-medium text-card-foreground">
              {t('agent.tools.gsc.title')}
            </span>
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {simplifyPage(result.pageUrl)}
          </div>
        </div>
        <Badge
          variant="outline"
          className="max-w-[42%] truncate border-border bg-muted/60 text-foreground"
        >
          {simplifyProperty(result.siteUrl)}
        </Badge>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <Badge variant="outline" className="border-border bg-muted/60 text-foreground">
          {t('agent.tools.gsc.last28Days')}
        </Badge>
        <Badge variant="outline" className="border-border bg-muted/60 text-foreground">
          {result.dateRange.startDate} - {result.dateRange.endDate}
        </Badge>
      </div>

      {showEmptyState ? (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
          <div className="mb-1 text-sm font-medium text-card-foreground">
            {isPropertyMismatch
              ? t('agent.tools.gsc.outsideProperty')
              : t('agent.tools.gsc.emptyTitle')}
          </div>
          <div className="text-xs leading-relaxed text-muted-foreground">
            {result.message || t('agent.tools.gsc.emptyDescription')}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <MetricTile
              label={t('agent.tools.gsc.metrics.clicks')}
              value={formatInteger(result.summary.clicks)}
              icon={MousePointerClick}
            />
            <MetricTile
              label={t('agent.tools.gsc.metrics.impressions')}
              value={formatInteger(result.summary.impressions)}
              icon={Eye}
            />
            <MetricTile
              label={t('agent.tools.gsc.metrics.ctr')}
              value={formatCtr(result.summary.ctr)}
              icon={ArrowUpRight}
            />
            <MetricTile
              label={t('agent.tools.gsc.metrics.position')}
              value={formatPosition(result.summary.averagePosition)}
              icon={Target}
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/20">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-card-foreground">
                {t('agent.tools.gsc.topQueries')}
              </span>
            </div>

            <div className="divide-y divide-border">
              {result.topQueries.map((queryResult) => (
                <div key={queryResult.query} className="px-3 py-2.5">
                  <div className="mb-2 text-sm font-medium text-card-foreground">
                    {queryResult.query}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                    <div>
                      {t('agent.tools.gsc.metrics.clicks')}: {formatInteger(queryResult.clicks)}
                    </div>
                    <div>
                      {t('agent.tools.gsc.metrics.impressions')}:{' '}
                      {formatInteger(queryResult.impressions)}
                    </div>
                    <div>
                      {t('agent.tools.gsc.metrics.ctr')}: {formatCtr(queryResult.ctr)}
                    </div>
                    <div>
                      {t('agent.tools.gsc.metrics.position')}:{' '}
                      {formatPosition(queryResult.averagePosition)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
